import { ORDER_STATUS_LABELS } from '../constants';

// Date formatting - Firestore Timestamp ve Date nesnelerini destekler
export const formatDate = (dateValue) => {
  if (!dateValue) return '-';

  let date;

  // Firestore Timestamp nesnesi mi kontrol et
  if (dateValue && typeof dateValue.toDate === 'function') {
    date = dateValue.toDate();
  } else if (dateValue instanceof Date) {
    date = dateValue;
  } else {
    date = new Date(dateValue);
  }

  // Geçersiz tarih kontrolü
  if (isNaN(date.getTime())) {
    return '-';
  }

  return date.toLocaleDateString('tr-TR');
};

// Tarih değerini input[type="date"] için YYYY-MM-DD formatına çevirir
export const formatDateForInput = (dateValue) => {
  if (!dateValue) return '';

  let date;

  // Firestore Timestamp nesnesi mi kontrol et
  if (dateValue && typeof dateValue.toDate === 'function') {
    date = dateValue.toDate();
  } else if (dateValue instanceof Date) {
    date = dateValue;
  } else {
    date = new Date(dateValue);
  }

  // Geçersiz tarih kontrolü
  if (isNaN(date.getTime())) {
    return '';
  }

  // YYYY-MM-DD formatına çevir
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

// Price formatting - Türk para birimi formatı
export const formatPrice = (price) => {
  const num = parseFloat(price || 0);

  // Türk para birimi formatı: 1.234,56
  return num.toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// Validation functions
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validatePhone = (phone) => {
  const phoneRegex = /^[0-9\s\-+()]{10,}$/;
  return phoneRegex.test(phone);
};

// Local storage helpers
export const setLocalStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error('Local storage error:', error);
  }
};

export const getLocalStorage = (key) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch (error) {
    console.error('Local storage error:', error);
    return null;
  }
};

// Error handling
export const handleFirebaseError = (error) => {
  console.error('Firebase error:', error);

  switch (error.code) {
    case 'auth/user-not-found':
      return 'Bu email adresi ile kayıtlı kullanıcı bulunamadı.';
    case 'auth/wrong-password':
      return 'Şifre hatalı.';
    case 'auth/invalid-email':
      return 'Geçersiz email adresi.';
    case 'auth/too-many-requests':
      return 'Çok fazla başarısız giriş denemesi. Lütfen daha sonra tekrar deneyin.';
    case 'permission-denied':
      return 'Firebase erişim izni yok. Lütfen Firebase güvenlik kurallarını kontrol edin.';
    case 'unavailable':
      return 'Firebase servisi şu anda kullanılamıyor. Lütfen daha sonra tekrar deneyin.';
    default:
      return 'Bir hata oluştu. Lütfen tekrar deneyin.';
  }
};

// Loading states
export const createLoadingState = () => ({
  isLoading: false,
  error: null,
  data: null
});

// Debounce function
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Tül Hesaplama Fonksiyonları
export const calculatePileDensity = (pileModel) => {
  const densityValues = {
    'Standart SEYREK': 2,
    'Standart ORTA': 2.5,
    'Standart SIK': 3,
    'S Pile': 3,
    'Amerikan Pile 2\'li': 3,
    'Amerikan Pile 3\'lü': 4,
    'Kapsül Pile': 3,
    'Çima Dikiş': 1,
    'Düz Dikiş': 1,
    'Kumaş Güneşlik': 1
  };

  return densityValues[pileModel] || 0;
};

export const calculateUsedM2 = (width, height, pileModel, customPileDensity = null) => {
  // CM değerlerini metre'ye çevir (100'e böl)
  const widthNum = (parseFloat(width) || 0) / 100;
  const heightNum = (parseFloat(height) || 0) / 100;

  // Özel modeller (Pile sıklığı kullanılmayanlar)
  switch (pileModel) {
    case 'Çima Dikiş':
      return widthNum + 0.75;
    case 'Düz Dikiş':
      return widthNum + 0.5;
    case 'Kumaş Güneşlik':
      return widthNum + 0.2;
  }

  // Eğer custom pile sıklığı verilmişse onu kullan, yoksa pile modelinden hesapla
  let pileDensity = customPileDensity !== null ? parseFloat(customPileDensity) : calculatePileDensity(pileModel);

  if (!pileDensity || pileDensity === 0) {
    return widthNum;
  }

  // Pile sıklığı olan modeller için
  return (widthNum * pileDensity) + 0.3;
};

export const calculateCapsulePrice = (usedM2, capsuleUnitPrice) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const capsuleUnitPriceNum = parseFloat(capsuleUnitPrice) || 0;

  // Metrekareyi bir üst tam sayıya yuvarla
  const roundedM2 = Math.ceil(usedM2Num);

  // Metrekarede 7 kapsül kullanılıyor
  const capsuleCount = roundedM2 * 7;

  return capsuleCount * capsuleUnitPriceNum;
};

export const calculateCapsuleCost = (usedM2, capsuleUnitCost) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const capsuleUnitCostNum = parseFloat(capsuleUnitCost) || 0;

  // Metrekareyi bir üst tam sayıya yuvarla
  const roundedM2 = Math.ceil(usedM2Num);

  // Metrekarede 7 kapsül kullanılıyor
  const capsuleCount = roundedM2 * 7;

  return capsuleCount * capsuleUnitCostNum;
};

export const calculateTotalPrice = (usedM2, unitPrice, extraPrice, pileModel, capsulePrice = 0, accessories = []) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitPriceNum = parseFloat(unitPrice) || 0;
  const extraPriceNum = parseFloat(extraPrice) || 0;
  const capsulePriceNum = parseFloat(capsulePrice) || 0;
  let totalAccessoryPrice = 0;
  if (Array.isArray(accessories)) {
    totalAccessoryPrice = accessories.reduce((sum, acc) => sum + (parseFloat(acc.total) || 0), 0);
  } else {
    totalAccessoryPrice = parseFloat(accessories) || 0;
  }

  const basePrice = usedM2Num * (unitPriceNum + extraPriceNum);
  
  if (pileModel === 'Kapsül Pile') {
    return basePrice + capsulePriceNum + totalAccessoryPrice;
  }

  return basePrice + totalAccessoryPrice;
};

export const calculateTotalCost = (usedM2, unitCost, extraCost = 0, pileModel = '', capsuleCost = 0, accessories = []) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitCostNum = parseFloat(unitCost) || 0;
  const extraCostNum = parseFloat(extraCost) || 0;
  const capsuleCostNum = parseFloat(capsuleCost) || 0;
  let totalAccessoryCost = 0;
  if (Array.isArray(accessories)) {
    totalAccessoryCost = accessories.reduce((sum, acc) => {
      if (acc.costTotal !== undefined && acc.costTotal !== '') {
        return sum + (parseFloat(acc.costTotal) || 0);
      }
      const c = parseFloat(acc.cost) || 0;
      const q = parseFloat(acc.quantity) || 1;
      const m = acc.unitType === 'metre' ? (parseFloat(acc.meter) || 1) : 1;
      return sum + (c * q * m);
    }, 0);
  } else {
    totalAccessoryCost = parseFloat(accessories) || 0;
  }

  const baseCost = usedM2Num * (unitCostNum + extraCostNum);

  if (pileModel === 'Kapsül Pile') {
    return baseCost + capsuleCostNum + totalAccessoryCost;
  }

  return baseCost + totalAccessoryCost;
};

/** Kanat astar ücreti: kullanılan mt × birim fiyat × adet */
export const calculateAstarPrice = (usedM, astarUnitPrice, quantity = 1) => {
  const used = parseFloat(usedM) || 0;
  const unit = parseFloat(astarUnitPrice) || 0;
  const qty = parseFloat(quantity) || 1;
  return used * unit * qty;
};

/** Kanat astar maliyeti: kullanılan mt × birim maliyet × adet */
export const calculateAstarCost = (usedM, astarUnitCost, quantity = 1) => {
  const used = parseFloat(usedM) || 0;
  const unit = parseFloat(astarUnitCost) || 0;
  const qty = parseFloat(quantity) || 1;
  return used * unit * qty;
};

export const calculateDiscountedPrice = (totalPrice, discount) => {
  const totalPriceNum = parseFloat(totalPrice) || 0;
  const discountNum = parseFloat(discount) || 0;

  if (discountNum <= 0) return totalPriceNum;

  const discountAmount = totalPriceNum * (discountNum / 100);
  return totalPriceNum - discountAmount;
};

// Stor ve Zebra Hesaplama Fonksiyonları
export const calculateStorZebraUsedM2 = (width, height) => {
  // CM değerlerini metre'ye çevir (100'e böl)
  let widthNum = (parseFloat(width) || 0) / 100;
  let heightNum = (parseFloat(height) || 0) / 100;

  // En için minimum 1 metre kontrolü
  if (widthNum < 1) {
    widthNum = 1;
  }

  // Boy: minimum 2m, 2m üzerinde 0.5m katlarına yukarı yuvarla
  heightNum = Math.max(2, Math.ceil(heightNum * 2) / 2);

  // En: 0.1m katlarına yukarı yuvarla
  widthNum = Math.ceil(widthNum * 10) / 10;

  return widthNum * heightNum;
};

export const calculateStorZebraTotalPrice = (usedM2, unitPrice, width, extras = {}, motorPriceTL = 0, mounting = 0) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitPriceNum = parseFloat(unitPrice) || 0;

  // Ekstra seçenekler için özel en hesaplaması
  let widthForExtras = (parseFloat(width) || 0) / 100; // cm -> metre
  if (widthForExtras > 0) {
    if (widthForExtras < 1) widthForExtras = 1;
    // 50cm (0.5m) katlarına yukarı yuvarla
    widthForExtras = Math.ceil(widthForExtras * 2) / 2;
  }

  const motorPriceTLNum = parseFloat(motorPriceTL) || 0;
  const mountingNum = parseFloat(mounting) || 0;

  const basePrice = usedM2Num * unitPriceNum;
  
  // Ekstra ücretleri hesaplanan en (widthForExtras) ile çarpıyoruz
  const extraCostsTotal = Object.values(extras).reduce((sum, val) => {
    const valNum = parseFloat(val) || 0;
    return sum + (valNum * widthForExtras);
  }, 0);

  return basePrice + extraCostsTotal + motorPriceTLNum + mountingNum;
};

export const calculateStorZebraTotalCost = (usedM2, unitCost, width, extras = {}, motorCostTL = 0, mountingCost = 0) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitCostNum = parseFloat(unitCost) || 0;
  const mountingCostNum = parseFloat(mountingCost) || 0;
  
  let widthForExtras = (parseFloat(width) || 0) / 100;
  if (widthForExtras > 0) {
    if (widthForExtras < 1) widthForExtras = 1;
    widthForExtras = Math.ceil(widthForExtras * 2) / 2;
  }

  const motorCostTLNum = parseFloat(motorCostTL) || 0;
  const baseCost = usedM2Num * unitCostNum;
  
  const extraCostsTotal = Object.values(extras).reduce((sum, val) => {
    const valNum = parseFloat(val) || 0;
    return sum + (valNum * widthForExtras);
  }, 0);

  return baseCost + extraCostsTotal + motorCostTLNum + mountingCostNum;
};

// Ahşap Jaluzi ve Metal Jaluzi Hesaplama Fonksiyonları
export const calculateJaluziUsedM2 = (width, height) => {
  // CM değerlerini metre'ye çevir (100'e böl)
  let widthNum = (parseFloat(width) || 0) / 100;
  let heightNum = (parseFloat(height) || 0) / 100;

  // En için minimum 1 metre kontrolü
  if (widthNum < 1) {
    widthNum = 1;
  }

  // Boy için minimum 1 metre kontrolü
  if (heightNum < 1) {
    heightNum = 1;
  }

  // 10'un katına yuvarlama
  widthNum = Math.ceil(widthNum * 10) / 10;
  heightNum = Math.ceil(heightNum * 10) / 10;

  return widthNum * heightNum;
};

export const calculateJaluziTotalPrice = (usedM2, unitPrice, mounting = 0, motorPrice = 0, zigilAmount = 0, ikiCamArasiAmount = 0) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitPriceNum = parseFloat(unitPrice) || 0;
  const mountingNum = parseFloat(mounting) || 0;
  const motorPriceNum = parseFloat(motorPrice) || 0;
  const zigilAmountNum = parseFloat(zigilAmount) || 0;
  const ikiCamArasiAmountNum = parseFloat(ikiCamArasiAmount) || 0;

  const basePrice = usedM2Num * unitPriceNum;

  return basePrice + mountingNum + motorPriceNum + zigilAmountNum + ikiCamArasiAmountNum;
};

export const calculateJaluziTotalCost = (usedM2, unitCost, motorCostTL = 0, zigilAmountCost = 0, ikiCamArasiAmountCost = 0, mountingCost = 0) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitCostNum = parseFloat(unitCost) || 0;
  const motorCostTLNum = parseFloat(motorCostTL) || 0;
  const zigilAmountCostNum = parseFloat(zigilAmountCost) || 0;
  const ikiCamArasiAmountCostNum = parseFloat(ikiCamArasiAmountCost) || 0;
  const mountingCostNum = parseFloat(mountingCost) || 0;

  const baseCost = usedM2Num * unitCostNum;

  return baseCost + motorCostTLNum + zigilAmountCostNum + ikiCamArasiAmountCostNum + mountingCostNum;
};

// Dikey Perde Hesaplama Fonksiyonu
export const calculateDikeyPerdeTotalPrice = (usedM2, unitPrice, mounting = 0, motorPriceTL = 0) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitPriceNum = parseFloat(unitPrice) || 0;
  const mountingNum = parseFloat(mounting) || 0;
  const motorPriceTLNum = parseFloat(motorPriceTL) || 0;

  const basePrice = usedM2Num * unitPriceNum;

  return basePrice + mountingNum + motorPriceTLNum;
};

export const calculateDikeyPerdeTotalCost = (usedM2, unitCost, motorCostTL = 0, mountingCost = 0) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitCostNum = parseFloat(unitCost) || 0;
  const motorCostTLNum = parseFloat(motorCostTL) || 0;
  const mountingCostNum = parseFloat(mountingCost) || 0;

  const baseCost = usedM2Num * unitCostNum;

  return baseCost + motorCostTLNum + mountingCostNum;
};

// Plise Hesaplama Fonksiyonu
export const calculatePliseUsedM2 = (width, height) => {
  // CM değerlerini metre'ye çevir (100'e böl)
  let widthNum = (parseFloat(width) || 0) / 100;
  let heightNum = (parseFloat(height) || 0) / 100;

  // 10'un katına yuvarlama
  widthNum = Math.ceil(widthNum * 10) / 10;
  heightNum = Math.ceil(heightNum * 10) / 10;

  // İlk çarpım hesaplama
  const calculatedArea = widthNum * heightNum;

  // Eğer çarpım 1'den büyükse, değerleri aynen kullan
  // Eğer çarpım 1'den küçükse, direkt 1 olarak ayarla
  if (calculatedArea >= 1) {
    return calculatedArea;
  } else {
    return 1;
  }
};

export const calculatePliseTotalPrice = (usedM2, unitPrice1 = 0, unitPrice2 = 0, mounting = 0, extraPrice = 0) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitPrice1Num = parseFloat(unitPrice1) || 0;
  const unitPrice2Num = parseFloat(unitPrice2) || 0;
  const mountingNum = parseFloat(mounting) || 0;
  const extraPriceNum = parseFloat(extraPrice) || 0;

  // Toplam birim fiyat (iki ürünün toplamı)
  const totalUnitPrice = unitPrice1Num + unitPrice2Num;

  // Eğer m2 0 ise (ölçü girilmemişse) varsayılan 1 m2 üzerinden hesapla 
  const effectiveM2 = usedM2Num > 0 ? usedM2Num : 1;

  const basePrice = effectiveM2 * totalUnitPrice;

  return basePrice + mountingNum + extraPriceNum;
};

export const calculatePliseTotalCost = (usedM2, unitCost1 = 0, unitCost2 = 0, mountingCost = 0, manualExtraCost = 0) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitCost1Num = parseFloat(unitCost1) || 0;
  const unitCost2Num = parseFloat(unitCost2) || 0;
  const mountingCostNum = parseFloat(mountingCost) || 0;
  const manualExtraCostNum = parseFloat(manualExtraCost) || 0;

  const totalUnitCost = unitCost1Num + unitCost2Num;
  const effectiveM2 = usedM2Num > 0 ? usedM2Num : 1;

  const baseCost = effectiveM2 * totalUnitCost;

  return baseCost + mountingCostNum + manualExtraCostNum;
};

// Aksesuar Hesaplama Fonksiyonu
export const calculateAksesuarTotalPrice = (unitPrice, quantity, usedMeter = 0, birimTipi = 'adet') => {
  const unitPriceNum = parseFloat(unitPrice) || 0;
  const quantityNum = parseFloat(quantity) || 0;

  if (birimTipi === 'metre') {
    const meterNum = parseFloat(usedMeter) || 0;
    return unitPriceNum * meterNum * quantityNum;
  }

  return unitPriceNum * quantityNum;
};

export const calculateAksesuarTotalCost = (unitCost, quantity, mountingCost = 0, usedMeter = 0, birimTipi = 'adet') => {
  const uCostNum = parseFloat(unitCost) || 0;
  const qtyNum = parseFloat(quantity) || 0;
  const mountNum = parseFloat(mountingCost) || 0;

  if (birimTipi === 'metre') {
    const meterNum = parseFloat(usedMeter) || 0;
    return (uCostNum * meterNum * qtyNum) + mountNum;
  }

  return (uCostNum * qtyNum) + mountNum;
};

// Katlamalı Perde Hesaplama Fonksiyonları
export const calculateKatlamaliPerdeUsedM = (width) => {
  // CM değerini metre'ye çevir ve 0.35 ekle
  const widthNum = (parseFloat(width) || 0) / 100;
  return widthNum + 0.35;
};

export const calculateKatlamaliPerdeTotalPrice = (usedM, fabricPrice = 0, mechanismPrice = 0, accessories = [], mounting = 0, motorPrice = 0) => {
  const usedMNum = parseFloat(usedM) || 0;
  const fabricPriceNum = parseFloat(fabricPrice) || 0;
  const mechanismPriceNum = parseFloat(mechanismPrice) || 0;
  const mountingNum = parseFloat(mounting) || 0;
  const motorPriceNum = parseFloat(motorPrice) || 0;

  // Aksesuar toplamını hesapla (dizi ise)
  let totalAccessoryPrice = 0;
  if (Array.isArray(accessories)) {
    totalAccessoryPrice = accessories.reduce((sum, acc) => sum + (parseFloat(acc.total) || 0), 0);
  } else {
    totalAccessoryPrice = parseFloat(accessories) || 0;
  }

  // Toplam Tutar = (Kullanılan metre x (Kumaş + Mekanizma)) + Aksesuarlar + Montaj + Motor
  const basePrice = usedMNum * (fabricPriceNum + mechanismPriceNum);

  return basePrice + totalAccessoryPrice + mountingNum + motorPriceNum;
};

export const calculateKatlamaliPerdeTotalCost = (usedM, fabricCost = 0, mechanismCost = 0, accessories = [], motorCost = 0, mountingCost = 0) => {
  const usedMNum = parseFloat(usedM) || 0;
  const fabricCostNum = parseFloat(fabricCost) || 0;
  const mechanismCostNum = parseFloat(mechanismCost) || 0;
  const motorCostNum = parseFloat(motorCost) || 0;
  const mountingCostNum = parseFloat(mountingCost) || 0;

  // Aksesuar toplamını hesapla (dizi ise)
  let totalAccessoryCost = 0;
  if (Array.isArray(accessories)) {
    totalAccessoryCost = accessories.reduce((sum, acc) => {
      const c = parseFloat(acc.cost) || 0;
      const q = parseFloat(acc.quantity) || 1;
      return sum + (c * q);
    }, 0);
  } else {
    totalAccessoryCost = parseFloat(accessories) || 0;
  }

  // Toplam Maliyet = (Kullanılan metre x (Kumaş + Mekanizma)) + Aksesuarlar + Motor
  const baseCost = usedMNum * (fabricCostNum + mechanismCostNum);

  return baseCost + totalAccessoryCost + motorCostNum + mountingCostNum;
};

// Rustik Hesaplama Fonksiyonu
export const calculateRustikTotalCost = (
  unitCost,
  ekAyakSayisi,
  ayakUcretiCost,
  mountingCost,
  usedCm,
  rustikRaySecenekleri = [],
  rustikAksesuarlar = []
) => {
  const uCost = parseFloat(unitCost) || 0;
  const ekAyakS = parseFloat(ekAyakSayisi) || 0;
  const ayakUCost = parseFloat(ayakUcretiCost) || 0;
  const mountCost = parseFloat(mountingCost) || 0;

  // Aksesuar ve Ray seçenekleri maliyet toplamı
  const accCostTotal = rustikAksesuarlar.reduce((sum, item) => sum + (parseFloat(item.cost) || 0) * (parseInt(item.quantity) || 1), 0);
  const usedCmNum = parseFloat(usedCm) || 0;
  const rayCostTotal = rustikRaySecenekleri.reduce((sum, item) => sum + (parseFloat(item.cost) || 0) * (usedCmNum / 100), 0);

  return uCost + (ekAyakS * ayakUCost) + accCostTotal + rayCostTotal + mountCost;
};

export const getOrderQuantity = (order) => {
  if (!order) return 0;
  
  // Plise siparişleri için tüm ölçülerin adetlerini topla
  if (order.productType === 'plise' && Array.isArray(order.pliseItems) && order.pliseItems.length > 0) {
    return order.pliseItems.reduce((sum, item) => {
      const q = parseFloat(item.quantity);
      return sum + (isNaN(q) ? 1 : q);
    }, 0);
  }
  
  // Diğer ürün tipleri için ana adet alanını kullan
  const qty = parseFloat(order.quantity);
  return isNaN(qty) ? 1 : qty;
};

// Dashboard İstatistik Hesaplama Fonksiyonları
export const calculateOrderStats = (orders) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const thisWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  const stats = {
    total: orders.reduce((sum, order) => sum + getOrderQuantity(order), 0),
    today: 0,
    thisWeek: 0,
    thisMonth: 0
  };

  orders.forEach(order => {
    // Sipariş tarihi varsa onu kullan, yoksa createdAt kullan
    const dateToUse = order.orderDate || order.createdAt;
    const createdAt = dateToUse?.toDate() || new Date(dateToUse);

    const qty = getOrderQuantity(order);

    if (createdAt >= today) {
      stats.today += qty;
    }
    if (createdAt >= thisWeek) {
      stats.thisWeek += qty;
    }
    if (createdAt >= thisMonth) {
      stats.thisMonth += qty;
    }
  });

  return stats;
};

export const calculateRevenueStats = (orders) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const thisWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  const stats = {
    total: 0,
    today: 0,
    thisWeek: 0,
    thisMonth: 0,
    average: 0
  };

  orders.forEach(order => {
    // İndirimli tutar varsa onu kullan, yoksa toplam tutarı kullan
    const orderAmount = order.discountedPrice && parseFloat(order.discount) > 0
      ? parseFloat(order.discountedPrice || 0)
      : parseFloat(order.totalPrice || 0);

    stats.total += orderAmount;

    // Siparişler için createdAt kullan
    const createdAt = order.createdAt?.toDate() || new Date(order.createdAt);

    if (createdAt >= today) {
      stats.today += orderAmount;
    }
    if (createdAt >= thisWeek) {
      stats.thisWeek += orderAmount;
    }
    if (createdAt >= thisMonth) {
      stats.thisMonth += orderAmount;
    }
  });

  stats.average = orders.length > 0 ? stats.total / orders.length : 0;

  return stats;
};

export const calculateProductTypeDistribution = (orders) => {
  const distribution = {};

  orders.forEach(order => {
    const productType = order.productType || 'unknown';
    distribution[productType] = (distribution[productType] || 0) + 1;
  });

  return distribution;
};

export const calculateOrderStatusDistribution = (customers) => {
  const distribution = {};

  // ORDER_STATUS_LABELS'dan tüm durumları al
  Object.values(ORDER_STATUS_LABELS).forEach(label => {
    distribution[label] = 0;
  });

  customers.forEach(customer => {
    const status = customer.orderStatus || 'new_order';
    const statusLabel = ORDER_STATUS_LABELS[status] || 'Yeni Sipariş';
    distribution[statusLabel] = (distribution[statusLabel] || 0) + 1;
  });

  return distribution;
};

export const calculateMonthlyCustomerGrowth = (customers) => {
  const monthlyData = {};

  customers.forEach(customer => {
    // Sipariş tarihi varsa onu kullan, yoksa createdAt kullan
    const dateToUse = customer.orderDate || customer.createdAt;
    const createdAt = dateToUse?.toDate() || new Date(dateToUse);
    const monthKey = `${createdAt.getFullYear()}-${String(createdAt.getMonth() + 1).padStart(2, '0')}`;

    monthlyData[monthKey] = (monthlyData[monthKey] || 0) + 1;
  });

  return monthlyData;
};

export const calculateMonthlyRevenue = (orders) => {
  const monthlyData = {};

  orders.forEach(order => {
    // Siparişler için createdAt kullan
    const createdAt = order.createdAt?.toDate() || new Date(order.createdAt);
    const monthKey = `${createdAt.getFullYear()}-${String(createdAt.getMonth() + 1).padStart(2, '0')}`;

    const orderAmount = order.discountedPrice && parseFloat(order.discount) > 0
      ? parseFloat(order.discountedPrice || 0)
      : parseFloat(order.totalPrice || 0);

    monthlyData[monthKey] = (monthlyData[monthKey] || 0) + orderAmount;
  });

  return monthlyData;
}; 